import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { SOURCE_LABELS, dateTime, dayAfter } from '@/lib/format'

/**
 * The order's history, read from the events the server records: each status
 * it reached, when, why, and who moved it there. The steps it has still to
 * go through are drawn ahead of it, greyed, so it reads like a parcel's
 * tracking page.
 *
 * Read-only. Nothing here changes the order.
 */

// How each status reads as something that happened to the order.
const REACHED = {
  new: 'Received',
  pending: 'Placed',
  confirmed: 'Confirmed',
  packed: 'Packed',
  partially_delivered: 'Partially delivered',
  dispatched: 'Dispatched',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
}

// The path an order takes when nothing goes wrong. Cancelled is not on it.
const PATH = ['new', 'pending', 'confirmed', 'packed', 'dispatched', 'delivered']

// Events that are not status changes, as one short line each.
const ACTIVITY = {
  item_added: 'Product added',
  item_corrected: 'Product updated',
  item_removed: 'Product removed',
  invoice_issued: 'Invoice issued',
  shipment_dispatched: 'Shipment dispatched',
  shipment_delivered: 'Shipment delivered',
  invoice_cancelled: 'Invoice cancelled',
  invoice_number_corrected: 'Invoice number corrected',
  invoice_completed: 'Invoice logo / Tamil names added',
  customer_edit: 'Changed by the customer',
  customer_edit_unreadable: 'Customer change not understood',
  lines_replaced_from_message: 'Lines replaced from a new message',
  delivery_update: 'Delivery updated',
  transport_chosen: 'Transport chosen by the customer',
  transport_added: 'Transport service added by the customer',
  transport_cleared: 'Transport removed by the customer',
  transport_updated: 'Transport updated',
  delivery_confirmation_sent: 'Asked to confirm delivery',
  delivery_reminder_sent: 'Delivery confirmation reminder sent',
  delivery_reminder_not_sent: 'Delivery confirmation message could not be sent',
  delivery_confirmed: 'Delivery confirmed',
  new_order_held: 'New order not taken — delivery not yet confirmed',
  placed: 'Placed by the customer',
  new_customer: 'New customer',
  message_not_read: 'Message could not be read',
}

// The customer's delivery confirmation is a step on the order's path, not
// background activity: it is the last thing that happens to an order.
const isStatusEvent = (e) =>
  [
    'created_from_message',
    'created_manual',
    'status_change',
    'superseded',
    'delivery_confirmed',
    'delivery_issue_reported',
  ]
    .includes(e.event_type)

const who = (e) => {
  if (e.actor_kind === 'staff') return e.actor_name || 'Staff'
  if (e.actor_kind === 'customer') return 'Customer'
  return 'System'
}

/**
 * Older customer edits named the line by its id — "line 292: quantity -> 99"
 * — which means nothing to anyone reading. The product code replaces it where
 * the line is still on the order, and arrows become words. The "customer via
 * order link:" prefix goes too: the entry already says Customer.
 */
const tidy = (text, codes) =>
  String(text)
    .replace(/^customer via order link:\s*/i, '')
    .replace(/^line (\d+) \(([^)]+)\)$/, '$2')
    .replace(/^line (\d+):\s*/, (_, id) => (codes[id] ? `${codes[id]} · ` : ''))
    .replace(/product -> /g, 'product changed to ')
    .replace(/unit -> (\S+)/g, (_, u) => `unit ${u.toLowerCase()}`)
    .replace(/(\w+) -> /g, '$1 ')

/** A note worth showing. The creation event stores machine JSON, not words. */
const noteOf = (e, codes) => {
  if (e.event_type === 'created_from_message') {
    try {
      const d = JSON.parse(e.detail)
      const source = SOURCE_LABELS[d.source] ?? 'message'
      const lines = d.line_count === 1 ? '1 line' : `${d.line_count} lines`
      return `From a WhatsApp ${source.toLowerCase()} · ${lines} read`
    } catch {
      return null
    }
  }
  if (e.event_type === 'created_manual') return 'Entered in the portal'
  if (e.event_type === 'item_corrected') {
    const note = correctionNote(e.detail)
    return note ? tidy(note, codes) : null
  }
  return e.detail ? tidy(e.detail, codes) : null
}

/**
 * Corrections saved before the server wrote them in words were stored as
 * "item 39: {'quantity': Decimal('1'), 'learn_alias': True}". The parts a
 * person cares about are read out of that; the line id and the flags are not
 * shown. Anything newer is already words and passes through.
 */
const correctionNote = (detail) => {
  const text = detail ?? ''
  if (!/^item \d+: \{/.test(text)) return text || null
  const parts = []
  if (/'product_id':/.test(text)) parts.push('product changed')
  const quantity = /'quantity': Decimal\('([\d.]+)'\)/.exec(text)
  if (quantity) parts.push(`quantity ${Number(quantity[1])}`)
  const unit = /'unit': '([^']+)'/.exec(text)
  if (unit) parts.push(`unit ${unit[1].toLowerCase()}`)
  const rate = /'rate': Decimal\('([\d.]+)'\)/.exec(text)
  if (rate) parts.push(`rate ₹${Number(rate[1]).toFixed(2)}`)
  if (/'notes':/.test(text)) parts.push('note updated')
  return parts.length ? parts.join(', ') : null
}

const titleOf = (e) => {
  if (e.event_type === 'created_from_message' || e.event_type === 'created_manual') {
    return 'Order received'
  }
  if (e.event_type === 'superseded') return 'Cancelled — replaced'
  if (e.event_type === 'delivery_issue_reported') return 'Items not delivered — reported'
  if (e.event_type === 'delivery_confirmed') {
    return /staff/i.test(e.detail ?? '')
      ? 'Delivery confirmed by admin'
      : 'Delivery confirmed by customer'
  }
  if (e.event_type === 'status_change') return REACHED[e.to_status] ?? e.to_status
  return ACTIVITY[e.event_type] ?? e.event_type.replace(/_/g, ' ')
}

// Exact time on hover, to the second; the row itself stays readable.
const exact = (iso) => {
  try {
    const text = String(iso)
    const zoned = /(Z|[+-]\d{2}:?\d{2})$/i.test(text) ? text : `${text}Z`
    return format(parseISO(zoned), 'dd MMM yyyy, HH:mm:ss')
  } catch {
    return ''
  }
}

export default function StatusTimeline({ order }) {
  const [showAll, setShowAll] = useState(false)
  const events = order.events ?? []

  const steps = events.filter((e) => showAll || isStatusEvent(e))
  // Line id -> product code, for older notes that only name the line.
  // A line removed since is no longer on the order, but its removal note
  // named it — "line 292 (MSBW18SWG)" — so that fills the gap.
  const codes = Object.fromEntries([
    ...events
      .map((e) => /line (\d+) \(([^)]+)\)/.exec(e.detail ?? ''))
      .filter(Boolean)
      .map((m) => [m[1], m[2]]),
    ...(order.items ?? []).map((i) => [String(i.id), i.item_code || i.raw_text]),
  ])

  // An order from before history was recorded still has a beginning.
  const hasStart = events.some(
    (e) => e.event_type === 'created_from_message' || e.event_type === 'created_manual',
  )
  const start = hasStart
    ? []
    : [{ id: 'start', at: order.created_at, event_type: 'created_manual', actor_kind: 'system', synthetic: true }]

  // What is still ahead on the normal path, shown greyed.
  // Partially delivered is a detour between Packed and Dispatched, not a
  // step every order takes: what is ahead of it is what is ahead of Packed.
  const at = PATH.indexOf(order.status === 'partially_delivered' ? 'packed' : order.status)
  const ahead = order.status === 'cancelled' || at < 0 ? [] : PATH.slice(at + 1)

  const otherCount = events.filter((e) => !isStatusEvent(e)).length

  // After Delivered on the path: the customer confirming every item arrived.
  // Outstanding once the order is out — they can confirm while it is still
  // Dispatched — and greyed ahead before that.
  const confirmed = Boolean(order.delivery_confirmed_at)
  // Only while Dispatched: once Delivered, by staff or by the customer's
  // tap, the delivery is confirmed.
  const awaiting = !confirmed && order.status === 'dispatched'
  const confirmAhead =
    !confirmed && !awaiting && order.status !== 'cancelled' && order.status !== 'delivered'

  const waitingStep = awaiting && (
          <li className="timeline-item timeline-waiting">
            <span className="timeline-dot" aria-hidden="true" />
            <div className="timeline-body">
              <div className="timeline-title">Waiting for customer confirmation</div>
              <div className="timeline-meta">
                {order.delivery_reminder_count > 0
                  ? `Reminded ${order.delivery_reminder_count}× · next ${dateTime(
                      dayAfter(order.delivery_reminder_sent_at),
                    )}`
                  : dayAfter(order.delivery_reminder_sent_at)
                    ? `First reminder ${dateTime(dayAfter(order.delivery_reminder_sent_at))}`
                    : 'Reminded every 24 hours from dispatch'}
              </div>
            </div>
          </li>
  )

  return (
    <div className="status-timeline">
      <div className="panel-title panel-title-row">
        <span>Status history</span>
        {otherCount > 0 && (
          <button type="button" className="btn-link" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Status only' : `Show all activity (${otherCount})`}
          </button>
        )}
      </div>

      <ol className="timeline">
        {[...start, ...steps].map((e) => {
          const status = isStatusEvent(e) || e.synthetic
          const note = e.synthetic ? null : noteOf(e, codes)
          // Shown red like a cancellation: something went wrong to put right.
          const cancelled =
            e.to_status === 'cancelled' || e.event_type === 'delivery_issue_reported'
          return (
            <li
              key={e.id}
              className={`timeline-item${status ? '' : ' timeline-activity'}${cancelled ? ' timeline-cancelled' : ''}`}
            >
              <span className="timeline-dot" aria-hidden="true" />
              <div className="timeline-body">
                <div className="timeline-title">{titleOf(e)}</div>
                <div className="timeline-meta" title={exact(e.at)}>
                  {dateTime(e.at)}
                  {!e.synthetic && <> · {who(e)}</>}
                </div>
                {note && <div className="timeline-note">{note}</div>}
              </div>
            </li>
          )
        })}

        {/* What happens next on a dispatched order — their tap is what makes
            it Delivered — so it sits before the greyed Delivered. */}
        {order.status === 'dispatched' && waitingStep}

        {ahead.map((s) => (
          <li key={`ahead-${s}`} className="timeline-item timeline-ahead">
            <span className="timeline-dot" aria-hidden="true" />
            <div className="timeline-body">
              <div className="timeline-title">{REACHED[s]}</div>
            </div>
          </li>
        ))}


        {confirmAhead && (
          <li className="timeline-item timeline-ahead">
            <span className="timeline-dot" aria-hidden="true" />
            <div className="timeline-body">
              <div className="timeline-title">Delivery confirmed</div>
            </div>
          </li>
        )}
      </ol>
    </div>
  )
}
